import { useMutation } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';

import { analytics } from 'analytics';
import { toaster } from 'ui/Toaster';

import { useApiContext } from '../../contexts/apiContext';
import {
  initialTeamSettings,
  TeamContext,
  TeamSettings,
} from '../../utils/team';
import { useCurrentUser } from '../../utils/user';
import { patchTeamSettings } from './utils';

type Props = {
  children: React.ReactNode;
};

const TeamProvider: React.FC<Props> = ({ children }) => {
  const { api } = useApiContext();
  const { user, refetchUser } = useCurrentUser();

  const { mutateAsync: saveSettings, isLoading: isSaving } = useMutation({
    mutationFn: async (settings: TeamSettings) => {
      try {
        await api().teams.partialUpdate({
          id: user?.default_team?.id ?? 0,
          patchedTeamRequest: { settings },
        });
        await refetchUser();
        toaster.success({ title: 'Corporate branding saved!' });
      } catch (error) {
        analytics.reportError('Error updating corporate branding', { error });
      }
    },
  });

  // The DB-stored team settings aren't strictly typed so we need to cast them to the correct type
  const savedTeamSettings = user?.default_team?.settings as
    | TeamSettings
    | undefined;

  // Check if any of the team setting fields have been set
  const hasInitialized = !isEmpty(savedTeamSettings);

  // Fill in any missing fields with the initial settings
  const teamSettings = useMemo(
    () => patchTeamSettings(initialTeamSettings, savedTeamSettings),
    [initialTeamSettings, savedTeamSettings]
  );

  return (
    <TeamContext.Provider
      value={{
        hasInitialized,
        isSaving,
        teamSettings,
        saveSettings,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export default TeamProvider;
